import React, { JSX, useCallback } from 'react';

import { Helmet } from 'react-helmet';
import { Route, Switch, useParams } from 'react-router';

import { Alert, AlertTitle, Box, Button, Card, CircularProgress, Container, Stack } from '@mui/material';

import AccommodationPropertyRatesSection from '~/components/Accommodation/Pages/PropertyDetails/sections/AccommodationPropertyRates/AccommodationPropertyRatesSection';

import { getProperty, getPropertyContent } from '~/services/AccommodationService';

import { useAccommodationServiceFetch } from '../../hooks/useAccommodationServiceFetch';

import AccommodationPropertyDetailsNav from './AccommodationPropertyDetailsNav';
import AccommodationPropertyDetailsOverviewPane from './AccommodationPropertyDetailsOverviewPane';
import AccommodationPropertyDetailsContextProvider from './context/AccommodationPropertyDetailsContext';
import AccommodationPropertyDetailsSection from './sections/AccommodationPropertyDetails/AccommodationPropertyDetailsSection';
import AccommodationPropertyFacilitiesSection from './sections/AccommodationPropertyFacilities/AccommodationPropertyFacilitiesSection';
import AccommodationPropertyRoomAmenitiesSection from './sections/AccommodationPropertyRoomAmenities/AccommodationPropertyRoomAmenitiesSection';
import AccommodationPropertyRoomMappingSection from './sections/AccommodationPropertyRoomMapping/AccommodationPropertyRoomMappingSection';
import AccommodationPropertyRoomsSection from './sections/AccommodationPropertyRooms/AccommodationPropertyRoomsSection';
import AccommodationPropertySuppliersSection from './sections/AccommodationPropertySuppliers/AccommodationPropertySuppliersSection';

export default function AccommodationPropertyDetailsPage() {
  const { propertyId } = useParams<{ propertyId: string }>();

  const getPropertyFetch = useCallback(() => getProperty({ propertyId }), [propertyId]);

  const { fetchReqState: propertyFetchState, refetch: refetchProperty } = useAccommodationServiceFetch({
    fetchFn: getPropertyFetch,
  });

  const ROUTES: Array<{ path: string; component: JSX.Element }> = [
    { path: '/accommodation/properties/:propertyId', component: <AccommodationPropertyDetailsSection /> },
    {
      path: '/accommodation/properties/:propertyId/suppliers',
      component: <AccommodationPropertySuppliersSection refreshProperty={refetchProperty} />,
    },
    { path: '/accommodation/properties/:propertyId/rooms', component: <AccommodationPropertyRoomsSection /> },
    {
      path: '/accommodation/properties/:propertyId/room-mapping',
      component: <AccommodationPropertyRoomMappingSection />,
    },
    { path: '/accommodation/properties/:propertyId/rates', component: <AccommodationPropertyRatesSection /> },
    { path: '/accommodation/properties/:propertyId/facilities', component: <AccommodationPropertyFacilitiesSection /> },
    {
      path: '/accommodation/properties/:propertyId/room-amenities',
      component: <AccommodationPropertyRoomAmenitiesSection />,
    },
  ];

  const getPropertyContentFetch = useCallback(() => getPropertyContent({ propertyId }), [propertyId]);
  const { fetchReqState: contentFetchState, refetch: refetchContent } = useAccommodationServiceFetch({
    fetchFn: getPropertyContentFetch,
  });

  if (propertyFetchState.status === 'pending' || propertyFetchState.status === 'uninitiated') {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress variant="indeterminate" />
      </Box>
    );
  }

  if (propertyFetchState.status === 'failed') {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => {
              refetchProperty();
            }}
          >
            Retry
          </Button>
        }
      >
        <AlertTitle>Failed to fetch the property.</AlertTitle>
        {JSON.stringify(propertyFetchState.error)}
      </Alert>
    );
  }

  if (contentFetchState.status === 'pending' || contentFetchState.status === 'uninitiated') {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress variant="indeterminate" />
      </Box>
    );
  }

  if (contentFetchState.status === 'failed') {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => {
              refetchContent();
            }}
          >
            Retry
          </Button>
        }
      >
        <AlertTitle>Failed to fetch the property content.</AlertTitle>
        {JSON.stringify(contentFetchState.error)}
      </Alert>
    );
  }

  return (
    <AccommodationPropertyDetailsContextProvider details={propertyFetchState.result} content={contentFetchState.result}>
      <Helmet>
        <title>Property | {propertyFetchState.result.name || ''}</title>
      </Helmet>
      <Container maxWidth="xl">
        <Stack direction="column" gap={4}>
          <AccommodationPropertyDetailsOverviewPane />

          <Box flexShrink={0}>
            <Card sx={{ position: 'sticky', top: 16 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <AccommodationPropertyDetailsNav />
              </Box>
            </Card>
          </Box>
          <Stack direction="column" flexGrow={1}>
            <Switch>
              {ROUTES.map(({ path, component }) => (
                <Route exact path={path} key={path}>
                  {component}
                </Route>
              ))}
            </Switch>
          </Stack>
        </Stack>
      </Container>
    </AccommodationPropertyDetailsContextProvider>
  );
}
